import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import './ToolPage.css'
import './RegexTester.css'
import { trackEvent } from '../../lib/analytics'
import { useDocumentMeta } from '../../hooks/useDocumentMeta'

const RegexTester = () => {
  useDocumentMeta({
    title: 'Testador de Regex Online | jarder.dev',
    description:
      'Teste expressões regulares em tempo real, com destaque dos ' +
      'matches e grupos de captura. Grátis, direto no navegador.',
  })

  const [pattern, setPattern] = useState('[A-Z]\\w+')
  const [flags, setFlags] = useState('g')
  const [text, setText] = useState(
    'Jarder Silva é Full-Stack e mora em Balsas.',
  )

  const { regex, error } = useMemo(() => {
    try {
      return { regex: new RegExp(pattern, flags.includes('g') ? flags : `${flags}g`), error: null }
    } catch (err) {
      return { regex: null, error: err.message }
    }
  }, [pattern, flags])

  const matches = useMemo(() => {
    if (!regex) return []
    try {
      return [...text.matchAll(regex)]
    } catch {
      return []
    }
  }, [regex, text])

  const segments = useMemo(() => {
    if (matches.length === 0) return [{ text, matched: false }]
    const parts = []
    let lastIndex = 0
    matches.forEach((match) => {
      if (match.index > lastIndex) {
        parts.push({ text: text.slice(lastIndex, match.index), matched: false })
      }
      parts.push({ text: match[0], matched: true })
      lastIndex = match.index + match[0].length
    })
    if (lastIndex < text.length) {
      parts.push({ text: text.slice(lastIndex), matched: false })
    }
    return parts
  }, [matches, text])

  const handleTest = () => {
    trackEvent('tool_generate', { tool: 'regex_tester' })
  }

  return (
    <div className="tool-page">
      <Link to="/ferramentas" className="tool-back-link">
        ← Todas as ferramentas
      </Link>
      <h1>Testador de Regex</h1>
      <p className="tool-disclaimer">
        Digite a expressão regular e o texto, os matches aparecem
        destacados ao vivo.
      </p>

      <div className="tool-row">
        <div className="tool-field" style={{ flex: 2 }}>
          <label htmlFor="pattern">Expressão regular</label>
          <input
            id="pattern"
            type="text"
            value={pattern}
            onChange={(e) => {
              setPattern(e.target.value)
              handleTest()
            }}
          />
        </div>
        <div className="tool-field">
          <label htmlFor="flags">Flags</label>
          <input
            id="flags"
            type="text"
            value={flags}
            onChange={(e) => setFlags(e.target.value)}
          />
        </div>
      </div>

      {error && <p className="tool-error">Regex inválida: {error}</p>}

      <div className="tool-field">
        <label htmlFor="test-text">Texto de teste</label>
        <textarea
          id="test-text"
          rows="6"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </div>

      <div className="regex-highlight">
        {segments.map((segment, i) => (
          <span
            key={i}
            className={segment.matched ? 'regex-match' : undefined}
          >
            {segment.text}
          </span>
        ))}
      </div>

      <div className="tool-result-card">
        <span className="tool-result-label">
          {matches.length} match{matches.length === 1 ? '' : 'es'}
        </span>
        {matches.length > 0 && (
          <ul className="tool-list" style={{ width: '100%' }}>
            {matches.map((match, i) => (
              <li key={i}>
                <strong>{match[0]}</strong>
                {match.length > 1 &&
                  ` (grupos: ${match.slice(1).map((g) => g ?? 'vazio').join(', ')})`}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="tool-info-section">
        <h2>Flags mais usadas</h2>
        <p>
          <code>g</code> (global) encontra todas as ocorrências, não só a
          primeira. <code>i</code> ignora maiúsculas/minúsculas.{' '}
          <code>m</code> faz <code>^</code> e <code>$</code> combinarem
          início e fim de cada linha, não só do texto inteiro. Os
          grupos de captura (parênteses no padrão) aparecem listados ao
          lado de cada match, úteis para extrair partes específicas do
          texto, como o código de área de um telefone ou o domínio de um
          e-mail.
        </p>
      </div>
    </div>
  )
}

export default RegexTester
